import React, {useMemo, useState} from 'react';
import {Button, ButtonGroup, Menu, MenuItem} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
    ctAllowedUiTransitions,
    ctStoredStatusForUi,
    ctUiStatusFromTask,
    statusLabel,
} from '../collectionTaskPayload';

/**
 * Split button showing the current CT workflow status with a dropdown of allowed
 * manual transitions. Assignee changes are handled separately (not via status).
 */
export default function CollectionTaskStatusButton({
    task,
    canManage = false,
    isAssignee = false,
    disabled = false,
    onTransition,
}) {
    const [anchorEl, setAnchorEl] = useState(null);

    const uiStatus = useMemo(() => ctUiStatusFromTask(task), [task]);
    const hasMaterials = (task?.materialsCount || 0) > 0;
    const transitions = useMemo(
        () => ctAllowedUiTransitions(uiStatus, {canManage, isAssignee, hasMaterials}),
        [uiStatus, canManage, isAssignee, hasMaterials],
    );

    const openMenu = (e) => setAnchorEl(e.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    const pick = (nextUiStatus) => {
        closeMenu();
        onTransition?.(ctStoredStatusForUi(nextUiStatus));
    };

    if (transitions.length === 0) {
        return (
            <Button
                variant="contained"
                disabled
                sx={{whiteSpace: 'nowrap'}}
                data-testid="ct-status-button"
            >
                {statusLabel(uiStatus)}
            </Button>
        );
    }

    return (
        <>
            <ButtonGroup
                variant="contained"
                disabled={disabled}
                data-testid="ct-status-button"
            >
                <Button sx={{whiteSpace: 'nowrap'}} onClick={openMenu}>
                    {statusLabel(uiStatus)}
                </Button>
                <Button
                    size="small"
                    aria-label="Change status"
                    data-testid="ct-status-arrow"
                    onClick={openMenu}
                >
                    <ArrowDropDownIcon/>
                </Button>
            </ButtonGroup>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                {transitions.map((next) => (
                    <MenuItem key={next} onClick={() => pick(next)}>
                        {statusLabel(next)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
